import { useState } from 'react';
import { clsx } from 'clsx';
import { usePdfExport } from '../../hooks/usePdfExport';
import { ExportPdfDialog } from './ExportPdfDialog';
import { PdfIcon } from './Icons';

interface PdfExportButtonProps {
  /** Button styling variant */
  variant?: 'primary' | 'secondary' | 'icon';
  /** Additional CSS classes */
  className?: string;
  /** Custom label text */
  label?: string;
  /** Character name for dialog display */
  characterName?: string;
}

/**
 * Reusable PDF export button with template selection dialog.
 *
 * When clicked:
 * - If only one template is available: exports directly
 * - If multiple templates: shows selection dialog
 *
 * This component encapsulates all PDF export logic to ensure
 * consistent behavior across the application.
 */
export function PdfExportButton({
  variant = 'secondary',
  className,
  label,
  characterName = 'Unnamed Character',
}: PdfExportButtonProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { exportPdf, isGenerating, availableTemplates, defaultTemplate } = usePdfExport();

  const handleClick = () => {
    if (availableTemplates.length === 0) {
      // No templates - shouldn't happen but handle gracefully
      return;
    } else if (availableTemplates.length === 1) {
      // Only one template - export directly
      exportPdf({ templateId: availableTemplates[0].id });
    } else {
      // Multiple templates - show dialog
      setIsDialogOpen(true);
    }
  };

  const handleExport = async (templateId: string) => {
    await exportPdf({ templateId });
  };

  // Determine button content based on variant
  const getButtonContent = () => {
    if (isGenerating) {
      if (variant === 'icon') {
        return (
          <>
            <SpinnerIcon className="w-5 h-5 animate-spin" />
            <span className="text-[10px] font-medium mt-0.5">...</span>
          </>
        );
      }
      return (
        <>
          <SpinnerIcon className="w-4 h-4 animate-spin" />
          {label || 'Generating...'}
        </>
      );
    }

    if (variant === 'icon') {
      return (
        <>
          <PdfIcon className="w-5 h-5" />
          <span className="text-[10px] font-medium mt-0.5">{label || 'PDF'}</span>
        </>
      );
    }

    return (
      <>
        <PdfIcon className="w-4 h-4" />
        {label || 'Export PDF'}
      </>
    );
  };

  // Determine button styles based on variant
  const getButtonStyles = () => {
    const base = 'flex items-center justify-center gap-2 transition-colors';

    if (variant === 'icon') {
      return clsx(
        base,
        'flex-col px-3 py-1.5 rounded-lg',
        isGenerating
          ? 'text-text-muted cursor-wait'
          : 'text-text-secondary hover:text-accent-gold hover:bg-background-secondary',
        className
      );
    }

    if (variant === 'primary') {
      return clsx(
        base,
        'btn-primary',
        isGenerating && 'cursor-wait',
        className
      );
    }

    // secondary (default)
    return clsx(
      base,
      'flex-1 px-4 py-2.5 rounded-lg bg-background-elevated font-medium',
      isGenerating
        ? 'text-text-muted cursor-wait'
        : 'text-text-primary hover:bg-background-secondary',
      className
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={isGenerating}
        className={getButtonStyles()}
        title={isGenerating ? 'Generating PDF...' : 'Export PDF'}
      >
        {getButtonContent()}
      </button>

      <ExportPdfDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onExport={handleExport}
        availableTemplates={availableTemplates}
        defaultTemplate={defaultTemplate}
        isGenerating={isGenerating}
        characterName={characterName}
      />
    </>
  );
}

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
